/**
 * What a user entered into a `wizard_preview` view (chosen journey, committed
 * answers and decisions), kept outside the component. The chat remounts a
 * message's views whenever its identity or position shifts (streamed message
 * replaced by the stored one after the conversation list reloads, history
 * reload, switching conversations, the card vs. its enlarged dialog), and
 * component-local state restarted from the spec's answers each time.
 *
 * Lives at module scope on purpose: it must outlive every chat remount.
 * Entries last for the page session only.
 */
import React from "react";
import type {
  AskWhen,
  AskWhom,
  WizardAnswer,
  WizardDecision,
  WizardPreviewView,
} from "./templateViewTypes.js";

export type WizardPreviewState = {
  journey: { askWhen: AskWhen; askWhom: AskWhom };
  answers: WizardAnswer[];
  decisions: WizardDecision[];
};

/**
 * Identity of one view, derived from its spec's content (a view has no stable
 * message id). Two identical views therefore share their state; the card and
 * its enlarged dialog sharing it is by design.
 */
export function wizardPreviewKey(view: WizardPreviewView): string {
  return JSON.stringify([
    view.templateId,
    view.title,
    view.askWhen,
    view.askWhom,
    view.unpublished,
    view.answers,
    view.decisions,
  ]);
}

export class WizardPreviewStore {
  private states = new Map<string, WizardPreviewState>();
  private listeners = new Map<string, Set<() => void>>();

  /** The stored state, or `initial` (the spec's own) when nothing was entered yet. */
  get(key: string, initial: WizardPreviewState): WizardPreviewState {
    return this.states.get(key) ?? initial;
  }

  update(
    key: string,
    initial: WizardPreviewState,
    change: (state: WizardPreviewState) => WizardPreviewState
  ): void {
    this.states.set(key, change(this.get(key, initial)));
    this.listeners.get(key)?.forEach(listener => listener());
  }

  subscribe(key: string, listener: () => void): () => void {
    const set = this.listeners.get(key) ?? new Set<() => void>();
    set.add(listener);
    this.listeners.set(key, set);
    return () => {
      set.delete(listener);
    };
  }

  clear(): void {
    this.states.clear();
  }
}

export const wizardPreviewStore = new WizardPreviewStore();

/** The view's state from the shared store; `update` writes through to it. */
export function useWizardPreviewState(
  key: string,
  initial: WizardPreviewState
): [
  WizardPreviewState,
  (change: (state: WizardPreviewState) => WizardPreviewState) => void,
] {
  const subscribe = React.useCallback(
    (listener: () => void) => wizardPreviewStore.subscribe(key, listener),
    [key]
  );
  const state = React.useSyncExternalStore(subscribe, () =>
    wizardPreviewStore.get(key, initial)
  );
  const update = React.useCallback(
    (change: (current: WizardPreviewState) => WizardPreviewState) =>
      wizardPreviewStore.update(key, initial, change),
    [key, initial]
  );
  return [state, update];
}
